
import React, { ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { getSidebarMenuItems } from '@/utils/sidebarMenuItems';
import { Toaster } from '@/components/ui/toaster';
import { Button } from '@/components/ui/button';
import { LogOut, Globe } from 'lucide-react';
import { useBusinessSettings } from '@/hooks/useBusinessSettings';
import { SidebarProvider } from '@/components/ui/sidebar';
import AdminSidebarNav from './AdminSidebarNav';

interface AdminLayoutProps {
  children: ReactNode;
  isLoading?: boolean;
  onLogout: () => void;
  userRole?: string;
  activeTab?: string;
}

const AdminLayout: React.FC<AdminLayoutProps> = ({
  children,
  isLoading = false,
  onLogout,
  userRole = '',
  activeTab = 'dashboard'
}) => {
  const navigate = useNavigate();
  const { settings } = useBusinessSettings();

  if (isLoading) {
    return <div className="flex items-center justify-center h-screen">
      <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-blue"></div>
    </div>;
  }

  return (
    <SidebarProvider>
      <div className="min-h-screen flex w-full">
        <AdminSidebarNav />
        
        <div className="flex-1 flex flex-col">
          <div className="bg-primary-blue text-white shadow-md">
            <div className="max-w-7xl mx-auto flex justify-between items-center py-3 px-6">
              <div className="flex flex-col">
                <h1 className="text-xl font-semibold text-white">Admin Dashboard</h1>
                {settings?.domain && <div className="flex items-center text-sm text-blue-200 mt-1">
                  <Globe className="h-4 w-4 mr-1" />
                  {settings.domain}
                </div>}
              </div>
              
              <div className="flex items-center gap-4">
                <div className="flex flex-col items-end">
                  {userRole && <span className="font-medium text-orange-300 text-xl">
                    {userRole.charAt(0).toUpperCase() + userRole.slice(1)} Account
                  </span>}
                </div>
                
                <Button 
                  variant="destructive" 
                  size="sm" 
                  onClick={onLogout} 
                  className="flex items-center gap-2"
                >
                  <LogOut className="h-4 w-4" />
                  Logout
                </Button>
              </div>
            </div>
          </div>
          
          <main className="flex-grow px-4 py-6">
            {children}
          </main>
          
          <Toaster />
        </div>
      </div>
    </SidebarProvider>
  );
};

export default AdminLayout;
